import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';
import { Api, useApiFormSubmit } from 'src/api';
import { Button, Modal, ModalBody, ModalFooter, Text } from 'src/components';
import { color } from 'src/constants/color.css';

type Props = {
  mode: 'create' | 'edit';
  /** The client the project belongs to, settled before the modal opens. */
  company: Api.Company.BaseView;
  /** The project being renamed, when editing. */
  project?: { id: string; name: string } | null;
  onClose: () => void;
  /** Refetches the summaries the Projects screen is built from. */
  invalidate: () => unknown;
};

const MAX_NAME_LENGTH = 100;

const NAME_HELP =
  'Shown on the Projects page and against every timer started on it.';

type FormValues = {
  name: string | null;
};

/**
 * Creates a project for a client, or renames one.  The client is already
 * settled when it opens; the name is the only thing left to type.
 */
export default function ProjectModal({
  mode,
  company,
  project,
  onClose,
  invalidate,
}: Props) {
  const isEdit = mode === 'edit';

  const submit = useApiFormSubmit(
    isEdit ? 'PUT' : 'POST',
    isEdit ? `/projects/${project?.id}` : '/projects',
    {
      onSuccess: () => {
        invalidate();
        onClose();
      },
    }
  );

  const form = useForm<FormValues>({
    defaultValues: {
      name: isEdit && project ? project.name : null,
    },
    onSubmit: (values, formObject) =>
      submit(
        isEdit
          ? { name: values.name }
          : { company: company['@id'], name: values.name },
        formObject
      ),
  });

  const name = form.values.name ?? '';
  const tooLong = name.length > MAX_NAME_LENGTH;
  const canSubmit = name.trim() !== '' && !tooLong && !form.isLoading;

  return (
    <Modal
      title={isEdit ? 'Edit project' : 'New project'}
      onClose={onClose}
      fullScreenOnMobile
    >
      <ModalBody>
        <html.div style={[styles.field]}>
          <FieldLabel>Client</FieldLabel>
          <html.input
            style={[styles.input, styles.inputDisabled]}
            value={company.name}
            disabled
          />
        </html.div>

        <html.div style={[styles.field]}>
          <FieldLabel>Project name</FieldLabel>
          <html.input
            style={[styles.input, tooLong && styles.inputError]}
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              form.getField('name').handleValueChange(e.target.value)
            }
            placeholder="e.g. Community Overhaul"
            autoFocus
          />
          <Text
            typeface="body.small.regular"
            color={tooLong ? 'accent' : 'neutral60'}
          >
            {tooLong
              ? `Keep the name to ${MAX_NAME_LENGTH} characters. That one is ${name.length}.`
              : NAME_HELP}
          </Text>
        </html.div>
      </ModalBody>

      <ModalFooter>
        <Button
          label="Cancel"
          variant="text"
          colorScheme="black"
          onClick={onClose}
        />
        <Button
          label={isEdit ? 'Save changes' : 'Create project'}
          onClick={form.handleSubmit}
          disabled={!canSubmit}
        />
      </ModalFooter>
    </Modal>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text typeface="body.small.regular" color="neutral80">
      {children}
    </Text>
  );
}

const styles = css.create({
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  input: {
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: color.neutral0,
    outline: 'none',
    width: '100%',
    '::placeholder': {
      color: color.neutral40,
    },
  },
  inputDisabled: {
    backgroundColor: color.neutral5,
    color: color.neutral80,
    cursor: 'default',
  },
  inputError: {
    borderColor: color.accent,
  },
});
